import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import QuickActions from "./athlete/QuickActions";
import OverviewPane from "./athlete/OverviewPane";
import TimelinePane from "./athlete/TimelinePane";
import TestingTab from "./athlete/TestingTab";
import MediaTab from "./athlete/MediaTab";
import FeedbackTab from "./athlete/FeedbackTab";
import DocumentsTab from "./athlete/DocumentsTab";
import AttendanceTab from "./athlete/AttendanceTab";
import ProgramsTab from "./athlete/ProgramsTab";
import MembershipTab from "./athlete/MembershipTab";
import AccountActivity from "./athlete/AccountActivity";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "testing", label: "Testing" },
  { key: "media", label: "Media" },
  { key: "feedback", label: "Feedback" },
  { key: "documents", label: "Documents" },
  { key: "attendance", label: "Attendance" },
  { key: "programs", label: "Programs" },
  { key: "membership", label: "Membership" },
  { key: "activity", label: "Account Activity" },
  { key: "timeline", label: "Timeline" },
];

export default function AthleteFile({ athleteId, onBack }) {
  const [tab, setTab] = useState("overview");
  const [athlete, setAthlete] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [lists, setLists] = useState({ coaches: [], parents: [], orgs: [] });
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadAll = useCallback(async () => {
    try {
      const [a, s, c, p, o, t] = await Promise.all([
        api.get(`/athletes/${athleteId}`),
        api.get(`/athletes/${athleteId}/snapshot`),
        api.get("/admin/users?role=coach"),
        api.get("/admin/users?role=parent"),
        api.get("/organizations"),
        api.get(`/athletes/${athleteId}/timeline`),
      ]);
      setAthlete(a.data); setSnapshot(s.data);
      setLists({ coaches: c.data, parents: p.data, orgs: o.data });
      setTimeline(t.data);
    } catch { toast.error("Failed to load athlete."); }
  }, [athleteId]);

  useEffect(() => { (async () => { setLoading(true); await loadAll(); setLoading(false); })(); }, [loadAll, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  if (loading || !athlete) return <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>;

  const type = (snapshot?.athlete_type || athlete.athlete_type || "in_person").replace("_", "-");

  return (
    <div data-testid="athlete-file">
      <button data-testid="af-back" onClick={onBack} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-xs uppercase tracking-widest font-bold mb-5 transition-colors"><ArrowLeft size={14} /> All Athletes</button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl">{athlete.name}</h1>
            <span data-testid="af-type-badge" className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-white/25 px-2.5 py-1 text-white/60">{type}</span>
          </div>
          <p className="text-white/40 text-sm mt-1">{[athlete.sport, athlete.position, athlete.grad_year && `Class of ${athlete.grad_year}`].filter(Boolean).join(" · ") || "—"}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_260px] gap-6 items-start">
        <div className="min-w-0">
          <div className="flex gap-1 border-b border-white/10 mb-8 overflow-x-auto" data-testid="af-tabs">
            {TABS.map((t) => (
              <button key={t.key} data-testid={`af-tab-${t.key}`} onClick={() => setTab(t.key)} className={`px-4 py-3 text-xs uppercase tracking-widest font-bold whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-white text-white" : "border-transparent text-white/40 hover:text-white"}`}>{t.label}</button>
            ))}
          </div>

          {tab === "overview" && <OverviewPane athleteId={athleteId} athlete={athlete} snapshot={snapshot} coaches={lists.coaches} parents={lists.parents} orgs={lists.orgs} onRefresh={refresh} />}
          {tab === "testing" && <TestingTab athleteId={athleteId} refreshKey={refreshKey} />}
          {tab === "media" && <MediaTab athleteId={athleteId} refreshKey={refreshKey} />}
          {tab === "feedback" && <FeedbackTab athleteId={athleteId} refreshKey={refreshKey} />}
          {tab === "documents" && <DocumentsTab athleteId={athleteId} refreshKey={refreshKey} />}
          {tab === "attendance" && <AttendanceTab athleteId={athleteId} refreshKey={refreshKey} />}
          {tab === "programs" && <ProgramsTab athleteId={athleteId} refreshKey={refreshKey} />}
          {tab === "membership" && <MembershipTab athlete={athlete} refreshKey={refreshKey} />}
          {tab === "activity" && <AccountActivity athleteId={athleteId} />}
          {tab === "timeline" && <TimelinePane timeline={timeline} />}
        </div>

        <QuickActions athlete={athlete} onRefresh={refresh} />
      </div>
    </div>
  );
}
